import api from './api';
import { getAxiosErrorMessage } from '../utils/error';
import { ApiResponse } from './user';
import { getColumnDisplayName, getPageActionDisplayName, getFormFieldDisplayName } from '../config/columns';
import {
  SERVICE_COLUMNS_MAP,
  start_columns,
  end_columns,
  ORDER_SERVICE_LABELS,
  getOrderFieldServiceKeys,
  isOrderBaseColumn,
  OrderServiceKey,
} from '../config/order-columns-config';
import {
  ORDER_SERVICE_ACCESS_OPTIONS,
  getOrderServiceAccessPermissionName,
} from '../config/order-service-access';

// 页面显示名称映射
const PAGE_NAMES: Record<string, string> = {
  'fee-management': '最新报价',
  'follow-up-management': '客户跟进',
  'trademark-monitor': '商标监控',
  'patent-payment-management': '专利年费监控',
  'status-translation': '状态翻译',
  'document-management': '文档管理',
  'order-management': '业务订单',
};

// 按服务聚合列，用于列权限展示分组
const ORDER_COLUMN_BUCKETS: Record<string, string[]> = (() => {
  const buckets: Record<string, string[]> = {};

  const collectColumns = (value: string[] | Record<string, string[]>): string[] => {
    const set = new Set<string>();
    if (Array.isArray(value)) {
      value.forEach(col => set.add(col));
    } else {
      Object.values(value || {}).forEach(arr => {
        (arr || []).forEach(col => set.add(col));
      });
    }
    return Array.from(set);
  };

  Object.entries(ORDER_SERVICE_LABELS).forEach(([service, label]) => {
    const config = (SERVICE_COLUMNS_MAP as any)[service];
    if (!config) return;
    buckets[label] = collectColumns(config as any);
  });

  return buckets;
})();

export interface Permission {
  id: number;
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
}

export interface RolePermission {
  role_id: number;
  permission_id: number;
  role_name: string;
  permission_name: string;
}

export interface PermissionCategory {
  category: string;
  permissions: Permission[];
}

export interface AssignPermissionRequest {
  role_id: number;
  permission_names: string[];
}

class PermissionService {
  // 获取所有权限
  async getPermissions(): Promise<ApiResponse<Permission[]>> {
    try {
      const response = await api.get('/permissions');
      return response.data;
    } catch (error) {
      return { success: false, message: '获取权限失败', data: [] };
    }
  }

  // 获取分类权限
  async getPermissionsByCategory(): Promise<ApiResponse<PermissionCategory[]>> {
    try {
      // 先尝试获取所有权限，然后按页面功能分组
      const response = await this.getPermissions();
      if (response.success && response.data) {
        // 按页面功能分组权限
        const pageGroups: Record<string, Permission[]> = {};

        // 页面分组配置
        const pageGroupConfig = {
          '业务订单': ['page.order-management'],
          '商标监控': ['page.trademark-monitor'],
          '专利年费监控': ['page.patent-payment-management'],
          '状态翻译': ['page.status-translation'],
          '最新报价': ['page.fee-management'],
          '客户跟进': ['page.follow-up-management'],
          '文档管理': ['page.document-management'],
          '用户权限': ['user.', 'role.'],
          '系统管理': ['system.', 'page.data-settings', 'page.notifications'],
          '其他': ['other']
        };

        response.data.forEach(permission => {
          let assigned = false;

          // 根据配置将权限分配到对应分组
          Object.entries(pageGroupConfig).forEach(([groupName, patterns]) => {
            if (assigned) return;

            const matches = patterns.some(pattern => {
              if (pattern === 'other') return false;
              return permission.name.includes(pattern);
            });

            if (matches) {
              if (!pageGroups[groupName]) {
                pageGroups[groupName] = [];
              }
              pageGroups[groupName].push(permission);
              assigned = true;
            }
          });

          // 未分配的权限放入"其他"分组
          if (!assigned) {
            if (!pageGroups['其他']) {
              pageGroups['其他'] = [];
            }
            pageGroups['其他'].push(permission);
          }
        });

        // 转换为PermissionCategory格式，并按重要性排序
        const sortOrder = ['业务订单', '商标监控', '状态翻译', '最新报价', '专利年费监控', '客户跟进', '文档管理', '用户权限', '系统管理', '其他'];
        const permissionCategories: PermissionCategory[] = sortOrder
          .filter(groupName => pageGroups[groupName] && pageGroups[groupName].length > 0)
          .map(groupName => ({
            category: groupName,
            permissions: pageGroups[groupName].sort((a, b) => {
              // 在每个分组内按权限类型排序：view -> action -> column -> form
              const getTypeOrder = (name: string) => {
                if (name.includes('.view')) return 1;
                if (name.includes('.action.')) return 2;
                if (name.includes('.column.')) return 3;
                if (name.includes('.form.')) return 4;
                return 5;
              };

              const orderA = getTypeOrder(a.name);
              const orderB = getTypeOrder(b.name);

              if (orderA !== orderB) return orderA - orderB;
              return a.name.localeCompare(b.name);
            })
          }));

        return { success: true, data: permissionCategories };
      }

      return { success: false, message: '获取权限失败', data: [] };
    } catch (error) {
      return { success: false, message: '获取分类权限失败', data: [] };
    }
  }

  // 获取角色权限
  async getRolePermissions(roleId: number): Promise<ApiResponse<Permission[]>> {
    try {
      // 使用正确的后端 API 端点
      const response = await api.get(`/roles/${roleId}`);

      // 检查响应格式，后端返回的是角色对象，包含permissions数组
      if (response.data?.success && response.data?.data?.permissions) {
        return { success: true, data: response.data.data.permissions };
      }

      // 如果没有权限数据，返回空数组
      return { success: true, data: [] };
    } catch (error: any) {
      console.error('获取角色权限失败:', error.response?.data || error);
      return { success: false, message: '获取角色权限失败', data: [] };
    }
  }

  // 为角色分配权限
  async assignRolePermissions(roleId: number, permissionNames: string[]): Promise<ApiResponse<any>> {
    try {
      // 使用正确的后端 API 端点和数据格式
      const response = await api.post(`/roles/${roleId}/assign-permissions`, {
        permissions: permissionNames
      });

      return response.data;
    } catch (error: any) {
      console.error('权限分配失败:', error.response?.data || error);
      const errorMessage = getAxiosErrorMessage(error, '分配权限失败');
      return { success: false, message: errorMessage, data: undefined };
    }
  }

  // 批量创建权限
  async batchCreatePermissions(permissions: Array<{
    name: string;
    display_name?: string;
    description?: string;
    category?: string;
  }>): Promise<ApiResponse<any>> {
    try {
      // 尝试多种批量创建的端点和格式
      const endpoints = [
        '/permissions/batch'
      ];

      const formats = [
        { permissions: permissions },
        { data: permissions },
        permissions
      ];

      for (const endpoint of endpoints) {
        for (const format of formats) {
          try {
            console.log(`尝试批量创建权限到 ${endpoint}`, format);
            const response = await api.post(endpoint, format);
            console.log('批量权限添加成功');
            return response.data;
          } catch (error: any) {
            console.log(`批量端点 ${endpoint} 失败:`, error.response?.status, error.response?.data);
            continue;
          }
        }
      }

      // 所有批量端点都失败
      throw new Error('所有批量创建端点都不可用');
    } catch (error: any) {
      console.error('批量创建权限失败:', error);
      throw error;
    }
  }

  // 创建权限
  async createPermission(permissionData: {
    name: string;
    display_name?: string;
    description?: string;
    category?: string;
  }): Promise<ApiResponse<Permission>> {
    try {
      // 使用标准的 Spatie Permission 格式，避免多次尝试
      const payload = {
        name: permissionData.name,
        guard_name: 'web',
        display_name: permissionData.display_name || permissionData.name,
        description: permissionData.description || `Permission for ${permissionData.name}`
      };

      console.log('Creating permission:', payload);
      const response = await api.post('/permissions', payload);
      console.log('Permission created successfully');
      return response.data;
    } catch (error: any) {
      console.error('All permission creation formats failed:', error.response?.data || error);
      const errorMessage = getAxiosErrorMessage(error, '创建权限失败');
      return { success: false, message: errorMessage, data: undefined };
    }
  }

  // 更新权限
  async updatePermission(id: number, permissionData: {
    name?: string;
    display_name?: string;
    description?: string;
    category?: string;
  }): Promise<ApiResponse<Permission>> {
    try {
      const response = await api.put(`/permissions/${id}`, permissionData);
      return response.data;
    } catch (error) {
      return { success: false, message: '更新权限失败', data: undefined };
    }
  }

  // 删除权限
  async deletePermission(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/permissions/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, message: '删除权限失败', data: undefined };
    }
  }

  // 同步权限到后台（批量创建页面权限）
  async syncPagePermissions(pagePermissions: {
    page_key: string;
    columns: string[];
    actions: string[];
    forms: { field_key: string; types: string[] }[];
    service_views?: { service_key: string; label: string }[];
  }[]): Promise<ApiResponse<any>> {
    try {
      const permissions: Array<{
        name: string;
        display_name: string;
        description: string;
        category: string;
      }> = [];

      // 为每个页面创建权限
      for (const page of pagePermissions) {
        const pageKey = page.page_key; // 保持原始格式

        // 页面访问权限
        permissions.push({
          name: `page.${pageKey}.view`,
          display_name: `访问${PAGE_NAMES[pageKey] || pageKey}页面`,
          description: `View ${pageKey} page`,
          category: 'page'
        });

        // 业务订单子菜单访问权限
        const serviceViews = page.service_views || (
          pageKey === 'order-management'
            ? ORDER_SERVICE_ACCESS_OPTIONS.map(option => ({ service_key: option.key, label: option.label }))
            : []
        );
        serviceViews.forEach(serviceView => {
          permissions.push({
            name: getOrderServiceAccessPermissionName(serviceView.service_key),
            display_name: `访问业务订单 - ${serviceView.label}`,
            description: `View order-management ${serviceView.service_key} service menu`,
            category: 'page'
          });
        });

        // 列权限：订单主表字段共用，扩展表字段按服务拆分
        for (const column of page.columns) {
          const columnDisplayName = getColumnDisplayName(pageKey, column);
          if (pageKey !== 'order-management') {
            permissions.push({
              name: `page.${pageKey}.column.${column}`,
              display_name: columnDisplayName,
              description: `View ${column} column`,
              category: 'column'
            });
            continue;
          }

          const serviceKeys = getOrderFieldServiceKeys(column);
          if (isOrderBaseColumn(column) || serviceKeys.length === 0) {
            const groupLabel = (() => {
              const baseFields = new Set<string>([
                ...start_columns,
                ...end_columns,
              ]);
              if (isOrderBaseColumn(column) || baseFields.has(column)) return '基本信息';
              for (const [label, list] of Object.entries(ORDER_COLUMN_BUCKETS)) {
                if (list.includes(column)) return label;
              }
              return '其他服务';
            })();
            permissions.push({
              name: `page.${pageKey}.column.${column}`,
              display_name: `${columnDisplayName}【${groupLabel}】`,
              description: `View ${column} column`,
              category: 'column'
            });
            continue;
          }

          serviceKeys.forEach((service: OrderServiceKey) => {
            permissions.push({
              name: `page.${pageKey}.column.${service}.${column}`,
              display_name: `${columnDisplayName}【${ORDER_SERVICE_LABELS[service]}】`,
              description: `View ${service}.${column} column`,
              category: 'column'
            });
          });
        }

        // 操作权限
        for (const action of page.actions) {
          const actionDisplayName = getPageActionDisplayName(pageKey, action);
          permissions.push({
            name: `page.${pageKey}.action.${action}`,
            display_name: actionDisplayName,
            description: `${action} action`,
            category: 'action'
          });
        }

        // 表单权限：订单主表字段共用，扩展表字段按服务拆分
        for (const form of page.forms) {
          for (const type of form.types) {
            if (type === 'view') {
              continue;
            }

            const fieldDisplayName = getFormFieldDisplayName(pageKey, form.field_key);
            const typeMap: Record<string, string> = {
              'view': '查看',
              'edit': '编辑'
            };
            if (pageKey !== 'order-management') {
              permissions.push({
                name: `page.${pageKey}.form.${form.field_key}.${type}`,
                display_name: `${fieldDisplayName} - ${typeMap[type] || type}`,
                description: `${type} ${form.field_key} field`,
                category: 'form'
              });
              continue;
            }

            const serviceKeys = getOrderFieldServiceKeys(form.field_key);
            if (isOrderBaseColumn(form.field_key) || serviceKeys.length === 0) {
              const baseFields = new Set<string>([
                ...start_columns,
                ...end_columns,
              ]);
              const field = form.field_key;
              const groupLabel = isOrderBaseColumn(field) || baseFields.has(field)
                ? '基本信息'
                : (Object.entries(ORDER_COLUMN_BUCKETS).find(([, list]) => list.includes(field))?.[0] || '其他服务');
              permissions.push({
                name: `page.${pageKey}.form.${form.field_key}.${type}`,
                display_name: `${fieldDisplayName}【${groupLabel}】 - ${typeMap[type] || type}`,
                description: `${type} ${form.field_key} field`,
                category: 'form'
              });
              continue;
            }

            serviceKeys.forEach((service: OrderServiceKey) => {
              permissions.push({
                name: `page.${pageKey}.form.${service}.${form.field_key}.${type}`,
                display_name: `${fieldDisplayName}【${ORDER_SERVICE_LABELS[service]}】 - ${typeMap[type] || type}`,
                description: `${type} ${service}.${form.field_key} field`,
                category: 'form'
              });
            });
          }
        }
      }

      // 创建所有权限，但排除页面权限分组中不需要的权限
      const basicPermissions = permissions.filter(p => {
        // 页面访问权限：仅匹配 page.xxx.view 与业务订单子菜单 view。
        const isPageViewPermission = p.name.match(/^page\.[^.]+\.view$/) !== null ||
          p.name.match(/^page\.order-management\.service\.[^.]+\.view$/) !== null;

        if (isPageViewPermission) {
          return true;
        }

        // 其他权限正常包含
        return p.name.includes('.action.') ||
               p.name.includes('.column.') ||
               p.name.includes('.form.');
      }); // 移除数量限制，包含所有权限类型

      console.log(`准备批量创建 ${basicPermissions.length} 个权限`);

      // 尝试批量创建权限
      try {
        const response = await this.batchCreatePermissions(basicPermissions);
        if (response.success) {
          return response;
        } else {
          console.log('批量创建失败，回退到逐个创建模式');
        }
      } catch (error) {
        console.log('批量创建API不可用，回退到逐个创建模式:', error);
      }

      // 回退方案：逐个创建权限（忽略已存在的）
      let successCount = 0;
      let errorCount = 0;

      // 分批处理，每批10个，减少并发压力
      const batchSize = 10;
      for (let i = 0; i < basicPermissions.length; i += batchSize) {
        const batch = basicPermissions.slice(i, i + batchSize);
        const promises = batch.map(async (permission) => {
          try {
            const response = await this.createPermission(permission);
            if (response.success) {
              return { success: true, permission: permission.name };
            } else {
              // 如果权限已存在，也算作成功
              if (response.message?.includes('already exists') ||
                  response.message?.includes('已存在') ||
                  response.message?.includes('duplicate') ||
                  response.message?.includes('already been taken') ||
                  response.message?.includes('PermissionAlreadyExists')) {
                return { success: true, permission: permission.name, exists: true };
              } else {
                return { success: false, permission: permission.name, error: response.message };
              }
            }
          } catch (error: any) {
            // 检查是否是422错误（权限已存在）
            if (error.response?.status === 422 &&
                (error.response?.data?.message?.includes('already been taken') ||
                 error.response?.data?.message?.includes('PermissionAlreadyExists') ||
                 error.response?.data?.exception?.includes('PermissionAlreadyExists'))) {
              return { success: true, permission: permission.name, exists: true };
            } else {
              return { success: false, permission: permission.name, error: error.message };
            }
          }
        });

        const results = await Promise.all(promises);
        results.forEach(result => {
          if (result.success) {
            successCount++;
            if (result.exists) {
              console.log(`权限已存在: ${result.permission}`);
            } else {
              console.log(`权限添加成功: ${result.permission}`);
            }
          } else {
            errorCount++;
            console.log(`权限创建失败: ${result.permission}, 原因: ${result.error}`);
          }
        });

        // 批次之间短暂延迟
        if (i + batchSize < basicPermissions.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      return {
        success: true,
        message: `权限同步完成：成功 ${successCount} 个，失败 ${errorCount} 个`,
        data: { successCount, errorCount, totalCount: basicPermissions.length }
      };

    } catch (error) {
      return { success: false, message: '同步权限失败', data: undefined };
    }
  }
}

export default new PermissionService();
